import React, { useState } from 'react';

import { ACCOUNT_PATHS } from '@/lib/accountPaths';
import { useAccountRecord } from '@/pages/account/AccountRecordContext';
import { accountCount, accountParagraphs } from '@/pages/account/accountModel';
import { AccountDialog } from '@/pages/account/AccountOverlays';
import {
  AccountCard,
  CardColumn,
  CheckField,
  NavLink,
  RadioField,
  Row,
  ScopeChip,
  SmallButton,
  StatusChip,
  TextAreaField,
  TextField,
} from '@/pages/account/primitives';
import { SettingsPageShell } from './SettingsPageShell';
import { SETTINGS_CARDS } from './settingsSearchIndex';

/**
 * AR-390 — Proposal Builder.
 *
 * D-ACCT-38 (Jason 2026-09-18) — PARKED. The page still renders, so an old
 * link and a bookmark both still work, but it is out of the Settings column
 * and out of the settings search. It is not gone: nothing here is deleted, and
 * it returns to the column when the product does.
 */
export const SettingsProposals: React.FC = () => {
  const { record } = useAccountRecord();
  const [termsOpen, setTermsOpen] = useState(false);
  const pr = record.prop;
  // The team default, when the owner has set one, is not the advisor's to edit.
  const forced = record.teamset.terms !== "Each advisor's own terms";
  const paragraphs = accountParagraphs(pr.termsText);

  const termsPreview = (
    <SmallButton onClick={() => setTermsOpen(true)}>Preview</SmallButton>
  );

  return (
    <SettingsPageShell page="proposals">
      <CardColumn>
        <AccountCard
          cardKey="propc"
          name={SETTINGS_CARDS.propContent.name}
          chip={<ScopeChip scope="personal" />}
          form={
            <>
              <TextAreaField path="prop.welcome" label="Welcome text" />
              <TextAreaField path="prop.closing" label="Closing text" />
            </>
          }
        >
          <Row label="Welcome text" value={pr.welcome} />
          <Row label="Closing text" value={pr.closing} last />
        </AccountCard>

        {forced ? (
          <AccountCard name={SETTINGS_CARDS.propTermsTeam.name} chip={<ScopeChip scope="team" />}>
            <Row
              label="Terms source"
              value={`Using the ${record.biz.brand} team default`}
              extra={
                <NavLink to={ACCOUNT_PATHS.team}>
                  Managed by the account owner under Team + Access →
                </NavLink>
              }
            />
            <Row label="Terms URL" value={pr.termsLink} />
            <Row
              label="Terms text"
              value={accountCount(paragraphs.length, 'paragraph', 'paragraphs')}
              control={termsPreview}
              last
            />
          </AccountCard>
        ) : (
          <AccountCard
            cardKey="propt"
            name={SETTINGS_CARDS.propTerms.name}
            chip={<ScopeChip scope="personal" />}
            form={
              <>
                <RadioField
                  path="prop.termsMode"
                  label="Terms source"
                  name="settings-terms-mode"
                  options={[
                    ['own', 'Your own terms'],
                    ['team', `The ${record.biz.brand} team default`],
                  ]}
                />
                <TextField path="prop.termsLink" label="Terms URL" />
                <TextAreaField path="prop.termsText" label="Terms text" />
              </>
            }
          >
            <Row
              label="Terms source"
              value={
                pr.termsMode === 'team'
                  ? `Using the ${record.biz.brand} team default`
                  : 'Your own terms'
              }
            />
            <Row label="Terms URL" value={pr.termsLink} />
            <Row
              label="Terms text"
              value={accountCount(paragraphs.length, 'paragraph', 'paragraphs')}
              control={termsPreview}
              last
            />
          </AccountCard>
        )}

        <AccountCard
          cardKey="propx"
          name={SETTINGS_CARDS.propClient.name}
          chip={<ScopeChip scope="personal" />}
          form={
            <>
              <CheckField path="prop.hidePrices" label="Hide prices from the client view" />
              <TextField path="prop.cta" label="Approval button text" />
            </>
          }
        >
          <Row
            label="Show prices to clients"
            chip={<StatusChip status={pr.hidePrices ? 'Off' : 'On'} />}
          />
          <Row label="Approval button text" value={pr.cta} last />
        </AccountCard>
      </CardColumn>

      {termsOpen ? (
        <AccountDialog
          title="Terms"
          sub="What the client reads before approving."
          onClose={() => setTermsOpen(false)}
          actions={<SmallButton onClick={() => setTermsOpen(false)}>Close</SmallButton>}
        >
          <div className="space-y-2.5 text-[14.5px] leading-relaxed text-gray-800 dark:text-gray-200">
            {paragraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </AccountDialog>
      ) : null}
    </SettingsPageShell>
  );
};

export default SettingsProposals;
